import React, { useState, useEffect } from 'react';
import { Users, Settings as SettingsIcon, Plus, CheckCircle, XCircle, Loader, AlertCircle, Key } from 'lucide-react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import ApiKeyManagement from './ApiKeyManagement';

const AdminSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // User creation form
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    role: 'salesperson',
  });

  // Password reset
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Edit user
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'salesperson',
  });
  const [savingUser, setSavingUser] = useState(false);

  // Environment settings
  const [ipagEnvironment, setIpagEnvironment] = useState('sandbox');
  const [switchingEnvironment, setSwitchingEnvironment] = useState(false);
  
  // Installment settings
  const [installmentSettings, setInstallmentSettings] = useState(null);
  const [maxInstallments, setMaxInstallments] = useState(12);
  const [interestFreeLimit, setInterestFreeLimit] = useState(6);
  const [tiers, setTiers] = useState([
    { minInstallments: 1, maxInstallments: 6, interestRate: 0 },
    { minInstallments: 7, maxInstallments: 9, interestRate: 2.99 },
    { minInstallments: 10, maxInstallments: 12, interestRate: 3.99 },
  ]);
  const [savingInstallments, setSavingInstallments] = useState(false);

  // Boleto settings
  const [boletoSettings, setBoletoSettings] = useState({ defaultDueDays: 3, maxDueDays: 4 });
  const [defaultDueDays, setDefaultDueDays] = useState(3);
  const [maxDueDays, setMaxDueDays] = useState(4);
  const [savingBoleto, setSavingBoleto] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
      loadIpagEnvironment();
      loadInstallmentSettings();
      loadBoletoSettings();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Falha ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadIpagEnvironment = async () => {
    try {
      const response = await apiClient.get('/admin/settings/ipag-environment');
      if (response.data.success) {
        setIpagEnvironment(response.data.data.environment);
      }
    } catch (err) {
      console.error('Falha ao carregar configurações:', err);
    }
  };

  const loadInstallmentSettings = async () => {
    try {
      const response = await apiClient.get('/admin/installment-settings');
      if (response.data.success && response.data.data) {
        const settings = response.data.data;
        setInstallmentSettings(settings);
        setMaxInstallments(settings.max_installments);
        setInterestFreeLimit(settings.interest_free_limit);
        if (settings.tiers && settings.tiers.length > 0) {
          setTiers(settings.tiers.map(t => ({
            minInstallments: t.minInstallments,
            maxInstallments: t.maxInstallments,
            interestRate: parseFloat(t.interestRate),
          })));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar configurações de parcelamento:', err);
    }
  };

  const loadBoletoSettings = async () => {
    try {
      const response = await fetch('/api/settings/boleto');
      const data = await response.json();
      if (data.success && data.data) {
        setBoletoSettings(data.data);
        setDefaultDueDays(data.data.defaultDueDays);
        setMaxDueDays(data.data.maxDueDays);
      }
    } catch (err) {
      console.error('Erro ao carregar configurações de boleto:', err);
    }
  };

  const saveBoletoSettings = async () => {
    setError(null);
    setSuccess(null);
    setSavingBoleto(true);

    try {
      const response = await apiClient.put('/settings/boleto', {
        defaultDueDays: parseInt(defaultDueDays),
        maxDueDays: parseInt(maxDueDays),
      });

      if (response.data.success) {
        setSuccess('Configurações de boleto salvas com sucesso!');
        setBoletoSettings(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar configurações de boleto');
    } finally {
      setSavingBoleto(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post('/admin/users', newUser);
      if (response.data.success) {
        setSuccess('Usuário criado com sucesso!');
        setShowCreateUser(false);
        setNewUser({
          username: '',
          email: '',
          password: '',
          fullName: '',
          role: 'salesperson',
        });
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Falha ao criar usuário');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/status`, {
        isActive: !currentStatus,
      });
      if (response.data.success) {
        setSuccess(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso`);
        loadUsers();
      }
    } catch (err) {
      setError('Falha ao atualizar status do usuário');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setResettingPassword(true);
      const response = await apiClient.post(`/admin/users/${resetPasswordUser.id}/reset-password`, {
        newPassword,
      });

      if (response.data.success) {
        setSuccess(`Senha redefinida com sucesso para ${resetPasswordUser.username}`);
        setResetPasswordUser(null);
        setNewPassword('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Falha ao redefinir senha');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserData({
      username: user.username,
      email: user.email,
      fullName: user.full_name,
      role: user.role,
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setSavingUser(true);
      const response = await apiClient.put(`/admin/users/${editingUser.id}`, editUserData);

      if (response.data.success) {
        setSuccess(`Usuário ${editUserData.username} atualizado com sucesso`);
        setEditingUser(null);
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Falha ao atualizar usuário');
    } finally {
      setSavingUser(false);
    }
  };

  const handleSwitchEnvironment = async (environment) => {
    setError(null);
    setSuccess(null);

    try {
      setSwitchingEnvironment(true);
      const response = await apiClient.post('/admin/settings/ipag-environment', {
        environment,
      });

      if (response.data.success) {
        setIpagEnvironment(environment);
        setSuccess(`Ambiente iPag alterado para ${environment === 'sandbox' ? 'Sandbox' : 'Produção'}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar ambiente iPag');
    } finally {
      setSwitchingEnvironment(false);
    }
  };

  const handleSaveInstallmentSettings = async () => {
    setError(null);
    setSuccess(null);

    try {
      setSavingInstallments(true);

      const payload = {
        maxInstallments,
        interestFreeLimit,
        tiers: tiers.map(t => ({
          minInstallments: parseInt(t.minInstallments),
          maxInstallments: parseInt(t.maxInstallments),
          interestRate: parseFloat(t.interestRate),
        })),
      };

      const response = installmentSettings
        ? await apiClient.put(`/admin/installment-settings/${installmentSettings.id}`, payload)
        : await apiClient.post('/admin/installment-settings', payload);

      if (response.data.success) {
        setSuccess('Configurações de parcelamento salvas com sucesso');
        await loadInstallmentSettings();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar configurações');
    } finally {
      setSavingInstallments(false);
    }
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    setTiers([...tiers, {
      minInstallments: lastTier ? lastTier.maxInstallments + 1 : 1,
      maxInstallments: lastTier ? lastTier.maxInstallments + 3 : 3,
      interestRate: 0,
    }]);
  };

  const removeTier = (index) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((_, i) => i !== index));
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index][field] = value;
    setTiers(newTiers);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="alert-error">
          <p className="text-red-200">Acesso negado. Privilégios de administrador necessários.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl text-heading">Configurações de Administrador</h1>
        <p className="mt-2 text-sm text-subheading">Gerenciar usuários e configurações do sistema</p>
      </div>

      {error && (
        <div className="mb-6 alert-error flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 alert-success flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-200">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-dark-600 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <Users className="inline-block w-5 h-5 mr-2" />
            Gerenciamento de Usuários
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <SettingsIcon className="inline-block w-5 h-5 mr-2" />
            Configurações do Sistema
          </button>
          <button
            onClick={() => setActiveTab('installments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'installments'
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <SettingsIcon className="inline-block w-5 h-5 mr-2" />
            Parcelamento
          </button>
          <button
            onClick={() => setActiveTab('boleto')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'boleto'
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <SettingsIcon className="inline-block w-5 h-5 mr-2" />
            Boleto
          </button>
          <button
            onClick={() => setActiveTab('apikeys')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'apikeys'
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <Key className="inline-block w-5 h-5 mr-2" />
            Chaves API
          </button>
        </nav>
      </div>

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Usuários</h2>
            <button
              onClick={() => setShowCreateUser(!showCreateUser)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Criar Usuário
            </button>
          </div>

          {/* Create User Form */}
          {showCreateUser && (
            <div className="mb-6 card card-body">
              <h3 className="text-lg font-semibold mb-4 text-white">Criar Novo Usuário</h3>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome de Usuário
                    </label>
                    <input
                      type="text"
                      required
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      required
                      value={newUser.fullName}
                      onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha
                    </label>
                    <input
                      type="password"
                      required
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="input-label">
                    Função
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="input"
                  >
                    <option value="salesperson">Vendedor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Criar Usuário
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateUser(false)}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Users List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Usuário
                    </th>
                    <th className="table-header-cell">
                      Email
                    </th>
                    <th className="table-header-cell">
                      Função
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {users.map((u) => (
                    <tr key={u.id} className="table-row">
                      <td className="table-cell">
                        <div className="text-sm font-medium text-white">{u.full_name}</div>
                        <div className="text-sm text-gray-400">@{u.username}</div>
                      </td>
                      <td className="table-cell text-gray-300">
                        {u.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            u.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {u.role === 'admin' ? 'Administrador' : 'Vendedor'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_active ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Ativo
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            Inativo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(u)}
                            className="px-3 py-1 rounded bg-primary-100 text-primary-700 hover:bg-primary-200"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                            className={`px-3 py-1 rounded ${
                              u.is_active
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {u.is_active ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            onClick={() => setResetPasswordUser(u)}
                            className="px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            Redefinir Senha
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-6">Ambiente iPag</h2>
          
          <div className="card card-body">
            <div className="mb-4">
              <p className="text-sm text-gray-300 mb-4">
                Alternar entre ambientes iPag sandbox (teste) e produção (real).
                <strong className="block mt-2 text-yellow-400">
                  Aviso: Mudar para produção processará pagamentos reais!
                </strong>
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => handleSwitchEnvironment('sandbox')}
                disabled={switchingEnvironment || ipagEnvironment === 'sandbox'}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                  ipagEnvironment === 'sandbox'
                    ? 'border-blue-500 bg-blue-900 bg-opacity-50 text-blue-300'
                    : 'border-dark-500 bg-dark-600 text-gray-300 hover:border-blue-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center justify-center gap-2">
                  {ipagEnvironment === 'sandbox' && <CheckCircle className="w-5 h-5" />}
                  <span>Sandbox (Teste)</span>
                </div>
              </button>

              <button
                onClick={() => handleSwitchEnvironment('production')}
                disabled={switchingEnvironment || ipagEnvironment === 'production'}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                  ipagEnvironment === 'production'
                    ? 'border-green-500 bg-green-900 bg-opacity-50 text-green-300'
                    : 'border-dark-500 bg-dark-600 text-gray-300 hover:border-green-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center justify-center gap-2">
                  {ipagEnvironment === 'production' && <CheckCircle className="w-5 h-5" />}
                  <span>Produção (Real)</span>
                </div>
              </button>
            </div>

            <div className="mt-6 p-4 bg-dark-600 rounded-lg">
              <p className="text-sm text-gray-300">
                <strong>Ambiente Atual:</strong>{' '}
                <span className={ipagEnvironment === 'production' ? 'text-green-400 font-semibold' : 'text-blue-400 font-semibold'}>
                  {ipagEnvironment === 'production' ? 'PRODUÇÃO' : 'SANDBOX'}
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-2">
                As credenciais de produção devem ser configuradas no arquivo .env antes de mudar para o modo de produção.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Installment Settings Tab */}
      {activeTab === 'installments' && (
        <div>
          <div className="card card-body">
            <h2 className="text-xl font-semibold text-white mb-6">Configurações de Parcelamento</h2>
            
            <div className="space-y-6">
              {/* Max Installments */}
              <div>
                <label className="input-label">
                  Número Máximo de Parcelas
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={maxInstallments}
                  onChange={(e) => setMaxInstallments(parseInt(e.target.value))}
                  className="input"
                />
                <p className="mt-1 text-sm text-gray-400">
                  Máximo de parcelas permitidas para pagamentos com cartão de crédito (1-24)
                </p>
              </div>

              {/* Interest Free Limit */}
              <div>
                <label className="input-label">
                  Limite de Parcelas Sem Juros
                </label>
                <input
                  type="number"
                  min="0"
                  max={maxInstallments}
                  value={interestFreeLimit}
                  onChange={(e) => setInterestFreeLimit(parseInt(e.target.value))}
                  className="input"
                />
                <p className="mt-1 text-sm text-gray-400">
                  Número de parcelas sem juros (0 até o máximo de parcelas)
                </p>
              </div>

              {/* Interest Rate Tiers */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-300">
                    Faixas de Taxa de Juros
                  </label>
                  <button
                    onClick={addTier}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm btn-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Faixa
                  </button>
                </div>

                <div className="space-y-3">
                  {tiers.map((tier, index) => (
                    <div key={index} className="flex gap-3 items-start p-4 bg-dark-600 rounded-lg">
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Min Parcelas
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={tier.minInstallments}
                            onChange={(e) => updateTier(index, 'minInstallments', parseInt(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm bg-dark-700 border border-dark-500 text-white rounded focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Max Parcelas
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={tier.maxInstallments}
                            onChange={(e) => updateTier(index, 'maxInstallments', parseInt(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm bg-dark-700 border border-dark-500 text-white rounded focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Taxa (% a.m.)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.01"
                            value={tier.interestRate}
                            onChange={(e) => updateTier(index, 'interestRate', parseFloat(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm bg-dark-700 border border-dark-500 text-white rounded focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      {tiers.length > 1 && (
                        <button
                          onClick={() => removeTier(index)}
                          className="mt-6 p-1.5 text-red-400 hover:bg-red-900 hover:bg-opacity-30 rounded"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-sm text-gray-400">
                  Configure diferentes taxas de juros para diferentes faixas de parcelamento
                </p>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t border-dark-600">
                <button
                  onClick={handleSaveInstallmentSettings}
                  disabled={savingInstallments}
                  className="btn-primary flex items-center gap-2 px-6 py-2.5"
                >
                  {savingInstallments ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Salvar Configurações
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Boleto Settings Tab */}
      {activeTab === 'boleto' && (
        <div>
          <div className="card card-body">
            <h2 className="text-xl font-semibold text-white mb-6">Configurações de Boleto</h2>
            
            <div className="space-y-6">
              {/* Default Due Days */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dias de Vencimento Padrão
                </label>
                <input
                  type="number"
                  min="1"
                  max={maxDueDays}
                  value={defaultDueDays}
                  onChange={(e) => setDefaultDueDays(e.target.value)}
                  className="block w-full max-w-xs px-3 py-2 border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-sm text-gray-400">
                  Número de dias padrão até o vencimento do boleto (será pré-selecionado no formulário)
                </p>
              </div>

              {/* Max Due Days */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dias de Vencimento Máximo
                </label>
                <input
                  type="number"
                  min={defaultDueDays}
                  max="30"
                  value={maxDueDays}
                  onChange={(e) => setMaxDueDays(e.target.value)}
                  className="block w-full max-w-xs px-3 py-2 border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-sm text-gray-400">
                  Número máximo de dias permitido para vencimento do boleto (limite de 30 dias)
                </p>
              </div>

              {/* Validation Messages */}
              {parseInt(defaultDueDays) > parseInt(maxDueDays) && (
                <div className="bg-red-900/20 border border-red-500 rounded-md p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-sm text-red-400">
                      O vencimento padrão não pode ser maior que o vencimento máximo
                    </p>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveBoletoSettings}
                  disabled={savingBoleto || parseInt(defaultDueDays) > parseInt(maxDueDays)}
                  className="px-6 py-2 bg-primary-500 text-dark-900 rounded-lg hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingBoleto ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Salvar Configurações
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Management Tab */}
      {activeTab === 'apikeys' && (
        <div>
          <ApiKeyManagement />
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Editar Usuário - {editingUser.full_name}
              </h3>
              <form onSubmit={handleSaveUser} className="space-y-4">
                <div>
                  <label htmlFor="editUsername" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome de Usuário
                  </label>
                  <input
                    type="text"
                    id="editUsername"
                    value={editUserData.username}
                    onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="editEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="editEmail"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="editFullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    id="editFullName"
                    value={editUserData.fullName}
                    onChange={(e) => setEditUserData({ ...editUserData, fullName: e.target.value })}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="editRole" className="block text-sm font-medium text-gray-700 mb-1">
                    Função
                  </label>
                  <select
                    id="editRole"
                    value={editUserData.role}
                    onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="salesperson">Vendedor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    disabled={savingUser}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={savingUser}
                    className="flex-1 px-4 py-2 bg-primary-500 text-dark-900 rounded-lg hover:bg-primary-600 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingUser ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Redefinir Senha - {resetPasswordUser.full_name}
              </h3>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setResetPasswordUser(null);
                      setNewPassword('');
                    }}
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {resettingPassword ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Redefinindo...
                      </>
                    ) : (
                      'Redefinir Senha'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
